import { useTranslation } from 'react-i18next';
import React, { useMemo, useCallback, useState, useEffect } from 'react';
import cx from 'clsx';
import { QueryClientProvider } from 'react-query';

import { IconRcAdd, IconRcCross } from '@/assets/common';
import { IconRcEyeOpen, IconRcEyeClosed } from '@/assets/thread';
import { SUPPORTED_LOCALE_TYPE } from '@/locales';

import { Modal, Switch, Tips, AntdSelect } from '@/components';
import {
  Channel,
  RichTextData,
  RichTextEditor,
  RichTextEditorHandle,
} from '@/components/RichTextEditor';
import { useCommentEligibility, useSwitch, useChannelPick } from '@/hooks';
import { devLog, queryClient } from '@/utils';

import { PostFooter } from '../PostFooter';
import style from './index.module.scss';
import { OriginalSwitch } from '../PostFooter/components/OriginalSwitch';
import { isNumber } from 'lodash';

type ThreadProps = {
  onConfirm: (data: EditorSubmitContentData) => Promise<void>;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  enablePaidPost?: boolean;
  language?: SUPPORTED_LOCALE_TYPE;
  initialPostData?: InitialPostData;
  hiddenEligibilityFields?: string[];
  channel?: Channel;
  hideChannelPicker?: boolean;
};
const CHARGE_OPTIONS = [0.1, 1, 10];

const { Option } = AntdSelect;

const DefaultThreadCount = 2;

export const Thread = ({
  onConfirm,
  disabled = false,
  className,
  placeholder,
  enablePaidPost = true,
  language,
  initialPostData,
  hiddenEligibilityFields,
  channel,
  hideChannelPicker,
}: ThreadProps) => {
  const { t } = useTranslation();

  const [editors, setEditors] = useState<
    Array<{
      editor: RichTextEditorHandle | null;
      id: number;
      invalid: boolean;
    }>
  >(
    Array(DefaultThreadCount)
      .fill(0)
      .map(i => ({
        editor: null,
        id: Math.random(),
        invalid: true,
      })),
  );
  const { on: isChargeMode, turn: setChargeMode } = useSwitch();
  const { on: isLoading, turn: setLoading } = useSwitch();
  const { on: isUploading, turn: setImageUploading } = useSwitch();
  const { on: disabledChangeCharge, turn: setDisabledChangeCharge } = useSwitch();
  const [currentEditorIdx, setCurrentEditorIdx] = useState(0);
  const [chargeFee, setChargeFee] = useState(CHARGE_OPTIONS[0]?.toString());
  const { channelId } = useChannelPick(
    initialPostData ? initialPostData?.channel || null : channel,
  );

  const [commentEligibility, setCommentEligibility] = useCommentEligibility();
  const { on: isOriginal, turn } = useSwitch();
  const [contentLengthMap, setContentLengthMap] = useState<Record<string, number>>({});
  const [forceEnableOriginSwitch, setForceEnableOriginSwitch] = useState(false);
  const editorsLength = editors.length;

  useEffect(() => {
    // 回填
    if (initialPostData) {
      // thread和pay_thread不能互相更改， pay_thread不允许更改价格
      setDisabledChangeCharge(true);
      // set Editors count
      if (initialPostData?.thread_count && initialPostData?.thread_count + 1 > 2) {
        setEditors(
          Array(initialPostData?.thread_count + 1)
            .fill(0)
            .map(i => ({
              editor: null,
              id: Math.random(),
              invalid: true,
            })),
        );
      }
      if (initialPostData?.permissions) {
        setCommentEligibility(initialPostData?.permissions);
      }
      if (isNumber(initialPostData?.price) && initialPostData?.price > 0) {
        setChargeMode(true);
        setChargeFee(String(initialPostData?.price / 10000));
      }
      if (initialPostData?.is_original) {
        // 强制设置isOriginal开关，因为回填thread时还没有获取到contentLengthMap，没发判断enableOriginSwitch，如果是原创可以强制打开开关
        setForceEnableOriginSwitch(true);
        turn(true);
      }
    }
  }, [initialPostData, setChargeMode, setCommentEligibility, setDisabledChangeCharge, turn]);

  const handleTabClick = useCallback(({ currentTarget }: React.MouseEvent<HTMLDivElement>) => {
    const { idx } = currentTarget.dataset;

    setCurrentEditorIdx(Number(idx));
  }, []);

  const handleAddClick = useCallback(() => {
    setEditors(pre =>
      pre.concat({
        editor: null,
        id: Math.random(),
        invalid: true,
      }),
    );

    if (currentEditorIdx + 1 === editorsLength) {
      setCurrentEditorIdx(currentEditorIdx + 1);
    }
  }, [currentEditorIdx, editorsLength]);

  const handleDeleteClick = useCallback(
    async (e: React.MouseEvent<SVGElement>) => {
      e.stopPropagation();
      const { currentTarget } = e;
      const { idx } = currentTarget.dataset;
      const nIdx = Number(idx);

      try {
        if (!editors[nIdx].invalid) {
          await Modal.confirm({
            title: `${t('Thread.xskn2j')}  ${nIdx + 1}/${editorsLength}`,
            content: t('Qa_Answer.c8tgk4'),
            confirmBtnText: t('Thread.xskn2j'),
          });
        }

        setEditors(pre => pre.filter((_, i) => i !== nIdx));

        if (currentEditorIdx >= nIdx) {
          setCurrentEditorIdx(pre => pre - 1);
        }
      } catch (error) {}
    },
    [editors, currentEditorIdx, editorsLength, t],
  );

  const handleDisabledChange = useCallback(
    (isDisabled: boolean, i: number) => {
      if (editors[i].invalid !== isDisabled) {
        const next = [...editors];
        next[i] = {
          ...next[i],
          invalid: isDisabled,
        };
        setEditors(next);
      }
    },
    [editors],
  );

  const handleSubmit = useCallback(async () => {
    try {
      const params: EditorSubmitContentData = {
        type: isChargeMode ? 'pay_thread' : 'thread',
        content: '',
        permissions: isChargeMode ? {} : commentEligibility,
        is_original: isOriginal,
        channel_id: channelId,
      };

      if (isChargeMode) {
        params.price = Number(chargeFee) * 10000;
      }

      const textDatas = await Promise.all(editors.map(x => x?.editor?.getSubmitContent()));
      const validTextDatas: RichTextData[] = [];

      const emptyTextPages = textDatas
        .reduce((s, d, i) => {
          if (d?.textContent || d?.image_ids?.length) {
            validTextDatas.push(textDatas[i]!);
            return s;
          }

          s.push(`Page ${i + 1} / ${editorsLength}`);
          return s;
        }, [] as string[])
        .join('，');

      if (emptyTextPages) {
        await Modal.confirm({
          title: isChargeMode ? t('Thread.noContentFress') : t('Qa_Answer.vpyg15'),
          content: t('Qa_Answer.emptyTipContent', { pages: emptyTextPages }),
          cancelBtnText: t('Qa_Answer.go-edit'),
          confirmBtnText: t('Thread.x8bj0w'),
          cancelBtnType: 'primary',
        });
      }

      if (!validTextDatas.length) {
        return;
      }

      setLoading(true);
      const threads = validTextDatas.map(x => ({
        content: x.textContent,
        entities: {
          ...(x.mentionsList.length ? { mentions: x.mentionsList } : {}),
          ...(x.hashtagsList.length ? { tags: x.hashtagsList } : {}),
          ...(x.urlsWidthIndices.length ? { url: x.urlsWidthIndices } : {}),
          ...(x.slashesList.length ? { channels: x.slashesList } : {}),
        },
        image_ids: x.image_ids,
        card_id: x.card_id,
      }));

      if (threads.length === 1) {
        Object.assign(params, {
          type: 'text',
          ...threads[0],
        });
      } else {
        const [firstArticle, ...thread_data] = threads;
        Object.assign(params, {
          ...firstArticle,
          thread_data,
        });
      }

      devLog('submit draw content', params);
      await onConfirm?.(params);
    } finally {
      setLoading(false);
    }
  }, [
    isChargeMode,
    commentEligibility,
    isOriginal,
    editors,
    setLoading,
    onConfirm,
    chargeFee,
    editorsLength,
    t,
    channelId,
  ]);

  const handelContentLengthChange = useCallback(
    (props: { id: number; length: number }) => {
      setContentLengthMap({ ...contentLengthMap, ...{ [props?.id]: props?.length } });
    },
    [contentLengthMap],
  );

  const enableOriginSwitch = useMemo(() => {
    const sumContentLength = Object.entries(contentLengthMap)?.reduce(
      (prv: number, cur: [string, number]) => {
        const value = cur?.[1];
        return value ? prv + value : prv;
      },
      0,
    );
    return sumContentLength >= 120;
  }, [contentLengthMap]);

  const isInValid = useMemo(() => !editors.length || editors.every(x => x.invalid), [editors]);

  return (
    <QueryClientProvider client={queryClient}>
      <div className={cx(style.container, className)}>
        {enablePaidPost ? (
          <div className={cx(style.chargeTitle, isChargeMode && style.isChargeMode)}>
            <Switch
              checked={isChargeMode}
              onChange={setChargeMode}
              disabled={disabled || disabledChangeCharge}>
              <span className={style.chargeHint}>{t('Thread.1rhod3j')}</span>
            </Switch>
            <Tips className={style.chargeTip} title={t('Thread.1h72lp8')} />
            {isChargeMode ? (
              <>
                <span className={style.chargeDesc}>
                  {t('Thread.adcws4')}
                  <span className={style.dollorSymbol}>$</span>
                </span>
                <AntdSelect
                  className={style.feeSelect}
                  disabled={disabled || disabledChangeCharge}
                  value={chargeFee}
                  onChange={setChargeFee as any}>
                  {CHARGE_OPTIONS.map(v => (
                    <Option key={v} className={style.feeSelectOption} value={v}>
                      {v}
                    </Option>
                  ))}
                </AntdSelect>
                <Tips className={style.chargeTip} title={t('Thread.183s8zx')} placement="right" />
              </>
            ) : null}
          </div>
        ) : null}
        <div className={style.main}>
          <div className={style.tabs}>
            {editors.map((_, i) => (
              <div
                className={cx(
                  style.tab,
                  i > 1 && style.hasCrossIcon,
                  i === currentEditorIdx && style.isSelected,
                )}
                key={_.id}
                onClick={handleTabClick}
                data-idx={i}>
                {i + 1} / {editorsLength}
                {i > 1 ? (
                  <IconRcCross
                    className={style.crossIcon}
                    onClick={handleDeleteClick}
                    data-idx={i}
                  />
                ) : null}
              </div>
            ))}
            {!disabled && editorsLength < 10 ? (
              <IconRcAdd className={style.addIcon} onClick={handleAddClick} />
            ) : null}
          </div>
          {isChargeMode ? (
            <div className={style.chargeModeDesc}>
              {currentEditorIdx ? (
                <>
                  <IconRcEyeClosed className={style.eyeIcon} />
                  <span>
                    {t('Thread.1qjvqx', { page: `${currentEditorIdx + 1} / ${editorsLength}` })}
                  </span>
                </>
              ) : (
                <>
                  <IconRcEyeOpen className={style.eyeIcon} />
                  <span>{t('Thread.contentDesc', { page: `1 / ${editorsLength}` })}</span>
                </>
              )}
            </div>
          ) : null}
          {editors.map((x, i) => (
            <RichTextEditor
              maxLength={600}
              editorClassName={style.textEditor}
              placeholderClassName={style.editorStyle}
              wrapperClassName={cx(style.editorWrapper, currentEditorIdx === i && style.isShown)}
              footer={false}
              onInvalidChange={isDisabled => handleDisabledChange(isDisabled, i)}
              placeholder={
                i
                  ? t('Qa_Answer.holderMore')
                  : !!placeholder
                    ? placeholder
                    : t('Qa_Answer.holderCommon')
              }
              disabled={disabled}
              autoFocus
              toolbar={!disabled}
              key={editors[i].id}
              ref={r => (editors[i].editor = r)}
              onImageUploading={setImageUploading}
              language={language}
              onContentLengthChange={length =>
                handelContentLengthChange({ id: editors[i].id, length })
              }
              initialPostData={i === 0 ? initialPostData : initialPostData?.threads?.[i - 1]}
            />
          ))}
        </div>
        <PostFooter
          disabled={disabled || isUploading || isInValid}
          isLoading={isLoading}
          key={initialPostData?.channel?.id}
          channelPickerDisabled={!!initialPostData?.channel}
          onSubmit={handleSubmit}
          showPaidEligibility={isChargeMode}
          hiddenEligibilityFields={hiddenEligibilityFields}
          slot={
            <OriginalSwitch
              isOriginal={isOriginal}
              onChange={turn}
              disabled={!(enableOriginSwitch || forceEnableOriginSwitch)}
            />
          }
          hideChannelPicker={hideChannelPicker}
        />
      </div>
    </QueryClientProvider>
  );
};
